import React, { useEffect, useRef } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomButton from 'shared/custom-button/CustomButton';
import { colors } from 'app/providers/theme/colors-type';
import styles from 'features/mite-policy/step-2/StepTwo.module.css';
import { IStepTwo } from 'entities/mite-policy/step-2/types/step-two-inerface';
import { useAppSelector } from 'shared/utils/hooks/useRedux';
import { FormFieldsStepOne } from 'entities/mite-policy/step-1/selector';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';

interface InsuredStepProps {
  formState: IStepTwo;
  errors: Record<string, string>;
  handleUpdateArrayItem: <K extends keyof IStepTwo>(
    fieldName: K,
    index: number,
    updatedItem: Partial<Extract<IStepTwo[K], any[]>[number]>
  ) => void;
  handleRemoveArrayItem: <K extends keyof IStepTwo>(fieldName: K, index: number) => void;
  handleAddArrayItem: <K extends keyof IStepTwo>(
    fieldName: K,
    newItem: IStepTwo[K] extends Array<infer U> ? U : never
  ) => void;
  handleNextStep: () => void;
  handleBack: () => void;
  expandedPanel: number | false;
  handleChangeAccordion: (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  handleUpdateInsuredField: (
    index: number,
    field: keyof IStepTwo['InsuredList'][0]
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUpdateInsuredDate: (
    index: number
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleParseInsuredFIO: (index: number, fio: string) => void;
  handleToggleInsuredGenderChange: (
    index: number
  ) => (e: React.MouseEvent<HTMLElement>, value: string | null) => void;
  handleCheckboxChange: (fieldName: keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  isSubmitted: boolean;
}

export const InsuredStep: React.FC<InsuredStepProps> = ({
  formState,
  errors,
  handleUpdateArrayItem,
  handleRemoveArrayItem,
  handleAddArrayItem,
  handleNextStep,
  handleBack,
  expandedPanel,
  handleChangeAccordion,
  handleUpdateInsuredField,
  handleUpdateInsuredDate,
  handleParseInsuredFIO,
  handleToggleInsuredGenderChange,
  handleCheckboxChange,
  isSubmitted
}) => {

  const stepOne = useAppSelector(FormFieldsStepOne);
  const isFirstMount = useRef(true);

  const isInsuredFullyFilled = (insured: IStepTwo['InsuredList'][0]) => {
    return insured.Fio && insured.BirthDate && insured.Gender;
  };

  const findFirstEmptyInsuredIndex = (insuredList: IStepTwo['InsuredList']) => {
    return insuredList.findIndex(insured => !isInsuredFullyFilled(insured));
  };

  const findNextEmptyInsuredIndex = (insuredList: IStepTwo['InsuredList'], currentIndex: number) => {
    for (let i = currentIndex + 1; i < insuredList.length; i++) {
      if (!isInsuredFullyFilled(insuredList[i])) {
        return i;
      }
    }
    return -1;
  };

  const hasErrorsOrEmptyFields = (index: number) => {
    if (!isSubmitted) return false;
    const insured = formState.InsuredList[index];
    const hasErrors =
      !!errors[`InsuredList[${index}].Fio`] ||
      !!errors[`InsuredList[${index}].BirthDate`] ||
      !!errors[`InsuredList[${index}].Gender`];
    const isEmpty = !isInsuredFullyFilled(insured);
    return hasErrors || isEmpty;
  };

  useEffect(() => {
    if (isFirstMount.current) {
      const firstEmptyIndex = findFirstEmptyInsuredIndex(formState.InsuredList);
      if (firstEmptyIndex !== -1) {
        handleChangeAccordion(firstEmptyIndex)({} as React.SyntheticEvent, true);
      }
      isFirstMount.current = false;
    }
  }, [formState.InsuredList, handleChangeAccordion]);

  useEffect(() => {
    if (isFirstMount.current) return;

    if (expandedPanel !== false) {
      const currentInsured = formState.InsuredList[expandedPanel];
      if (currentInsured && isInsuredFullyFilled(currentInsured)) {
        const nextEmptyIndex = findNextEmptyInsuredIndex(formState.InsuredList, expandedPanel);
        if (nextEmptyIndex !== -1) {
          handleChangeAccordion(nextEmptyIndex)({} as React.SyntheticEvent, true);
        }
      }
    }
  }, [formState.InsuredList, expandedPanel, handleChangeAccordion]);

  return (
    <Box>
      <Box className={styles.titleBox}>
        <h4>Застрахованные</h4>
        <p>Те, для кого оформляется полис</p>
      </Box>
      {Object.keys(errors).length > 0 && errors.InsuredListDuplicate && (
        <Box className={styles.redBox}>
          <span className={styles.iconRed}><CancelIcon /></span>
          <p>{errors.InsuredListDuplicate}</p>
        </Box>
      )}

      <Box className={styles.fullWidthBox}>
        <FormControlLabel
          control={
            <Checkbox
              name="PolicyholderIsSame"
              checked={Boolean(formState.PolicyholderIsSame)}
              onChange={handleCheckboxChange('PolicyholderIsSame')}
              sx={{
                color: 'grey.500',
                '&.Mui-checked': {
                  color: colors.primary,
                },
              }}
            />
          }
          label="Оформляю для себя"
        />
      </Box>
      <Box>
        {formState.InsuredList.map((insured, index) => (
          <Accordion
            key={index}
            expanded={expandedPanel === index}
            onChange={handleChangeAccordion(index)}
            sx={{
              border: hasErrorsOrEmptyFields(index) ? '1px solid red' : 'none',
              borderRadius: '4px',
              marginBottom: '8px',
              boxShadow: hasErrorsOrEmptyFields(index) ? '0 0 0 2px rgba(255, 0, 0, 0.2)' : 'none',
              backgroundColor: hasErrorsOrEmptyFields(index) ? 'rgba(255, 240, 240, 0.5)' : 'inherit',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" width="100%">
                <Typography flexGrow={1}>
                  {insured.Fio ? insured.Fio : `Застрахованный ${index + 1}`}
                </Typography>
                {index !== 0 && (
                  <DeleteIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveArrayItem('InsuredList', index);
                    }}
                    className={styles.deleteIcon}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box className={styles.userBox}>
                <TextField
                  name={`Fio-${index}`}
                  label="ФИО"
                  value={insured.Fio}
                  onChange={handleUpdateInsuredField(index, 'Fio')}
                  onBlur={(e) => handleParseInsuredFIO(index, e.target.value)}
                  error={!!errors[`InsuredList[${index}].LastName`] ||
                    !!errors[`InsuredList[${index}].FirstName`] ||
                    !!errors[`InsuredList[${index}].MiddleName`]
                  }
                  helperText={errors[`InsuredList[${index}].LastName`] ||
                    errors[`InsuredList[${index}].FirstName`] ||
                    errors[`InsuredList[${index}].MiddleName`]
                  }
                  className={styles.fullWidth}
                />
              </Box>
              <Box className={styles.secondLineBox}>
                <TextField
                  name={`BirthDate-${index}`}
                  label="Дата рождения"
                  variant="outlined"
                  value={insured.BirthDate}
                  onChange={handleUpdateInsuredDate(index)}
                  placeholder="дд.мм.гггг"
                  error={!!errors[`InsuredList[${index}].BirthDate`]}
                  helperText={errors[`InsuredList[${index}].BirthDate`]}
                />
                <ToggleButtonGroup
                  value={insured.Gender}
                  exclusive
                  onChange={handleToggleInsuredGenderChange(index)}
                  aria-label={`Gender-${index}`}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    '& .MuiToggleButtonGroup-grouped': {
                      flex: 1,
                      minWidth: 0,
                    },
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      borderRadius: '4px !important',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      flex: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      height: '40px !important'
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${colors.primary} !important`,
                      color: 'white !important',
                      '&:hover': {
                        backgroundColor: `${colors.primary} !important`,
                      },
                    },
                  }}
                >
                  <ToggleButton value="Ж" aria-label="Женщина">
                    Женщина
                  </ToggleButton>
                  <ToggleButton value="М" aria-label="Мужчина">
                    Мужчина
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box className={styles.errorGenderBox}>
                {errors[`InsuredList[${index}].Gender`] && (
                  <Typography color="error" variant="caption">
                    {errors[`InsuredList[${index}].Gender`]}
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
        {formState.InsuredList.length < 5 && (
          <IconButton
            onClick={() =>
              handleAddArrayItem('InsuredList', {
                Fio: '',
                LastName: '',
                FirstName: '',
                MiddleName: '',
                BirthDate: '',
                Gender: '',
              })
            }
            color="primary"
            aria-label="add"
          >
            <AddIcon />
            <Typography>Добавить застрахованного</Typography>
          </IconButton>
        )}

      </Box>
      {Object.keys(errors).length > 0 && (
        <Box className={styles.errorBox}>
          <p>Откройте вкладку застрахованных и заполните поля</p>
        </Box>
      )}

      <Box className={styles.colorBox}>
        <span className={styles.icon}><InfoIcon /></span>
        <p>Обратите внимание, на первом шаге вы указали <span>{stepOne.InsuranceCount}</span> застрахованных</p>
      </Box>

      <Box className={styles.btnBoxNext}>
        <CustomButton
          title="Назад"
          handleClick={handleBack}
          textColor={colors.primary}
          backgroundColor="none"
          width="150px"
          gap="10px"
          borderRadius='4px'
          padding='3px'
          border='none'
        />
        <CustomButton
          title="Далее"
          handleClick={handleNextStep}
          textColor="white"
          backgroundColor={colors.primary}
          width="150px"
          gap="10px"
          borderRadius='4px'
          padding='3px'
        />
      </Box>
    </Box>
  );
};